import React, { useCallback, useState, useEffect } from 'react';
import { PapiAttachmentFile, PapiDeviation } from '@wix/da-papi-types';
import { useDispatch, useSelector } from 'react-redux';
import { DndSortable, DndSortableElement } from '@wix/da-shared-react/pkg/Dnd/DndSortable';
import { DraggableType } from '@wix/da-shared-react/pkg/types/dnd';
import { repositionAttachment } from '@wix/da-shared-react/pkg/redux/uploads/actions';
import { getIsAttachmentDeviationLocked } from '@wix/da-shared-react/pkg/redux/uploads/selectors';
import DndProviderWrapper from '@wix/da-shared-react/pkg/Dnd/DndProviderWrapper';
import AttachmentAddButton from '../AttachmentAddButton';
import AttachmentThumb from '../AttachmentThumb';
import AttachmentThumbSlider from '../AttachmentThumbSlider';
import AttachmentThumbDragLayer from '../AttachmentThumbDragLayer';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { getAdditionalAttachmentType } from '../../../utils/additionalMediaUpload';

interface Props {
  className?: string;
  deviation?: PapiDeviation;
  selectedItemId?: number;
  onItemClick?: (attachment: PapiAttachmentFile) => void;
  isUploading: boolean;
  removeAttachment?: (fileId: number, callback?: () => void) => void;
  onAttachmentAdded?: (attachment: PapiAttachmentFile, deviation: PapiDeviation) => void;
}

const SortableAttachmentThumbSlider: React.FC<Props> = ({
  className,
  deviation,
  selectedItemId,
  onItemClick,
  isUploading,
  removeAttachment,
  onAttachmentAdded,
}) => {
  const dispatch = useDispatch();
  const isMultiVideo = useFlag('multi_video');

  // we keep a copy of deviation attachments locally, for quicker updates on drag
  const [attachments, setAttachments] = useState(deviation?.extended?.additionalMedia || []);

  const selectedIndex = attachments.findIndex(attachment => attachment.fileId === selectedItemId);

  const isDeviationBusy = useSelector(state =>
    getIsAttachmentDeviationLocked(state, deviation!.deviationId)
  );

  const isSortable = !isDeviationBusy && !isUploading;

  // but we want to make sure we do not have stale data whenever deviation is updated
  useEffect(() => {
    setAttachments(deviation?.extended?.additionalMedia || []);
  }, [deviation]);

  // While dragging, we're only updating local attachments
  const handleDrag = useCallback(
    (fileId: number, position: number) => {
      if (!attachments) {
        return;
      }
      // find the attachment with fileId in attachments array, move it to a new position, and update the attachments in the state
      const attachment = attachments.find(a => a.fileId === fileId);
      if (!attachment) {
        return;
      }
      const newAttachments = attachments.filter(a => a.fileId !== fileId);
      newAttachments.splice(position, 0, attachment);
      setAttachments(newAttachments);
    },
    [attachments]
  );

  // When dropping, we're actually updating the attachment position on the server
  const handleDrop = useCallback(
    (fileId: number, position: number) => {
      const attachment = attachments?.find(a => a.fileId === fileId);
      deviation &&
        attachment &&
        dispatch(
          repositionAttachment(
            attachment.fileId!,
            deviation!.deviationId!,
            getAdditionalAttachmentType(isMultiVideo),
            // Position is not zero indexed on the backend
            position + 1
          )
        );
    },
    [dispatch, deviation, attachments, isMultiVideo]
  );

  return (
    <DndProviderWrapper>
      <DndSortable onDrop={handleDrop} onDragOver={handleDrag}>
        {({ draggedElementId, ...dndProps }) => (
          <>
            <AttachmentThumbDragLayer attachments={attachments} />
            <AttachmentThumbSlider
              disableArrows={!!draggedElementId}
              className={className}
              scrollToIndex={selectedIndex === -1 ? attachments.length + 1 : selectedIndex}
              isSortable={isSortable}
            >
              {attachments.map((attachment, index) => {
                const isDragging = draggedElementId === attachment.fileId;
                const isSelected = selectedItemId === attachment.fileId;
                return (
                  <DndSortableElement
                    dndType={DraggableType.STUDIO_PREVIEW_THUMB}
                    id={attachment.fileId!}
                    key={attachment.fileId!}
                    position={index}
                    customPreview
                    {...dndProps}
                    isSortable={isSortable}
                  >
                    {sortableOptions => (
                      <div ref={sortableOptions.ref}>
                        <AttachmentThumb
                          draggable={isSortable}
                          deviation={deviation}
                          attachment={attachment}
                          removeAttachment={removeAttachment}
                          selected={isSelected}
                          dragging={isDragging}
                          onClick={() => onItemClick?.(attachment)}
                        />
                      </div>
                    )}
                  </DndSortableElement>
                );
              })}
              <AttachmentAddButton deviation={deviation} onAttachmentAdded={onAttachmentAdded} />
            </AttachmentThumbSlider>
          </>
        )}
      </DndSortable>
    </DndProviderWrapper>
  );
};
SortableAttachmentThumbSlider.displayName = 'SortableAttachmentThumbSlider';

export default SortableAttachmentThumbSlider;
